import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation } from 'react-router';
import { ruRU } from '@mui/x-data-grid-pro/locales';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
	getIsOrdersCollectionGroupListLoadingSelector,
	getIsOrdersCollectionWayListLoadingSelector,
	getOrdersCollectionGroupListSelector,
	getOrdersCollectionWayListSelector,
} from '../../selectors';
import {
	getOrdersCollectionGroupListAction,
	getOrdersCollectionWayListAction,
	setCollectionGroupOpenAction,
	setCollectionWayOpenAction,
} from '../../actions';
import { CustomNoRowsOverlay } from '../../../../components/data-grid/utils';
import { normalizeDataGridModel } from '../../../../utils/normalizeDataGridModel';
import { TABLE_ORDERS_COLLECTION_COLUMNS } from '../../constants';
import { GroupContextComponent } from '../group-context';
import { WayContextComponent } from '../way-context';
import './styles.less';

const OrdersWrapper = ({
	collection,
	onGetOrdersCollectionGroupList,
	ordersGroupList,
	isOrdersCollectionGroupListLoading,
	onGetOrdersCollectionWayList,
	ordersWayList,
	isOrdersCollectionWayListLoading,
	modes,
	type,
	row,
	onSetCollectionGroupOpen,
	onSetCollectionWayOpen,
}) => {
	const { search } = useLocation();

	const query = new URLSearchParams(search);

	useEffect(() => {
		if (type === 'group') {
			onGetOrdersCollectionGroupList({ row, collection, searchQuery: query.toString() });
			onSetCollectionGroupOpen(collection);
		}

		if (type === 'way') {
			onGetOrdersCollectionWayList({ row, collection, searchQuery: query.toString() });
			onSetCollectionWayOpen(collection);
		}
	}, [type, collection.id]);

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('childrenSorts', jsonSorts);

		if (type === 'group') {
			onGetOrdersCollectionGroupList({ row, collection, searchQuery: query.toString() });
		}

		if (type === 'way') {
			onGetOrdersCollectionWayList({ row, collection, searchQuery: query.toString() });
		}
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			let jsonFilters = [];

			if (type === 'group') {
				jsonFilters = JSON.stringify(
					normalizeDataGridModel(filterModel, TABLE_ORDERS_COLLECTION_COLUMNS(modes)),
				);
			}

			if (type === 'way') {
				jsonFilters = JSON.stringify(
					normalizeDataGridModel(filterModel, TABLE_ORDERS_COLLECTION_COLUMNS(modes)),
				);
			}

			query.set('childrenFilters', jsonFilters);

			if (type === 'group') {
				onGetOrdersCollectionGroupList({ row, collection, searchQuery: query.toString() });
			}

			if (type === 'way') {
				onGetOrdersCollectionWayList({ row, collection, searchQuery: query.toString() });
			}
		}
	});

	let list = [];
	let columns = [];
	let isLoading = false;
	let contextComponent = null;

	const [columnVisibilityModel, setColumnVisibilityModel] = useState({
		address_from: false,
		cost: false,
		comment: false,
	});

	const [selectedRows, setSelectedRows] = useState({
		type: 'include',
		ids: new Set(),
	});

	const [selectedRow, setSelectedRow] = useState(null);

	const [contextMenu, setContextMenu] = useState(null);

	const handleContextMenu = event => {
		event.preventDefault();

		if (selectedRows.ids.size === 0) {
			setSelectedRow(event.currentTarget.getAttribute('data-id'));
		}

		setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
	};

	const handleClose = () => {
		setContextMenu(null);
	};

	if (type === 'group') {
		list = ordersGroupList ?? [];
		isLoading = Boolean(isOrdersCollectionGroupListLoading.find(el => el === collection.id));
		columns = TABLE_ORDERS_COLLECTION_COLUMNS(modes);
		contextComponent = (
			<GroupContextComponent
				contextMenu={contextMenu}
				setContextMenu={setContextMenu}
				selectedOrders={selectedRows.ids.size !== 0 ? [...selectedRows.ids] : [selectedRow]}
				group={row}
			/>
		);
	}

	if (type === 'way') {
		list = ordersWayList ?? [];
		isLoading = Boolean(isOrdersCollectionWayListLoading.find(el => el === collection.id));
		columns = TABLE_ORDERS_COLLECTION_COLUMNS(modes);
		contextComponent = (
			<WayContextComponent
				contextMenu={contextMenu}
				setContextMenu={setContextMenu}
				selectedOrders={selectedRows.ids.size !== 0 ? [...selectedRows.ids] : [selectedRow]}
				way={row}
			/>
		);
	}

	const [columnModel, setColumnModel] = useState({});

	const handleColumnWidthChange = params => {
		setColumnModel(prev => ({
			...prev,
			[params.colDef.field]: params.width,
		}));
	};

	const columnsWithSavedWidth = columns.map(col => ({
		...col,
		width: columnModel[col.field] || col.width,
		flex: columnModel[col.field] ? 0 : col.flex,
	}));

	return (
		<>
			<DataGridPro
				key={`orders-${type} - ${collection.id}`}
				slots={{
					noRowsOverlay: CustomNoRowsOverlay,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
					row: {
						onContextMenu: handleContextMenu,
						style: { cursor: 'context-menu' },
					},
				}}
				componentsProps={{
					root: {
						onContextMenu: e => {
							e.preventDefault();
							handleClose();
						},
					},
				}}
				initialState={{ pinnedColumns: { left: ['empty', '__check__', 'id', 'actions'] } }}
				rows={list}
				columns={columnsWithSavedWidth}
				onColumnWidthChange={handleColumnWidthChange}
				hideFooterPagination
				hideFooter
				rowHeight={40}
				columnHeaderHeight={40}
				loading={isLoading}
				localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
				className="no-border"
				sortingMode="server"
				onSortModelChange={handleSortChange}
				filterMode="server"
				filterDebounceMs={300}
				onFilterModelChange={handleFilterChange}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
				checkboxSelection={!row.process}
				onRowSelectionModelChange={selection => setSelectedRows(selection)}
				disableMultipleColumnsSorting
				disableRowSelectionOnClick
			/>

			{contextComponent}
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	ordersGroupList: getOrdersCollectionGroupListSelector(),
	ordersWayList: getOrdersCollectionWayListSelector(),
	isOrdersCollectionGroupListLoading: getIsOrdersCollectionGroupListLoadingSelector(),
	isOrdersCollectionWayListLoading: getIsOrdersCollectionWayListLoadingSelector(),
});

const mapDispatchToProps = {
	onGetOrdersCollectionGroupList: getOrdersCollectionGroupListAction,
	onGetOrdersCollectionWayList: getOrdersCollectionWayListAction,
	onSetCollectionGroupOpen: setCollectionGroupOpenAction,
	onSetCollectionWayOpen: setCollectionWayOpenAction,
};

export const OrdersComponent = connect(mapStateToProps, mapDispatchToProps)(OrdersWrapper);

OrdersWrapper.propTypes = {
	collection: PropTypes.object.isRequired,
	onGetOrdersCollectionGroupList: PropTypes.func,
	onGetOrdersCollectionWayList: PropTypes.func,
	ordersGroupList: PropTypes.array,
	ordersWayList: PropTypes.array,
	isOrdersCollectionGroupListLoading: PropTypes.array.isRequired,
	isOrdersCollectionWayListLoading: PropTypes.array.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	onResetState: PropTypes.func,
	type: PropTypes.string.isRequired,
	row: PropTypes.object.isRequired,
	onSetCollectionGroupOpen: PropTypes.func.isRequired,
	onSetCollectionWayOpen: PropTypes.func.isRequired,
};
